'use client'

import { createContext, useContext, useState, useEffect } from 'react'

const defaultHamburgerOpen: boolean = false

type HamburgerContextType = {
  isOpen: boolean
  toggleIsOpen: () => void
  setIsOpen: (open: boolean) => void
}

const defaultHamburgerContext: HamburgerContextType = {
  isOpen: defaultHamburgerOpen,
  toggleIsOpen: () => null,
  setIsOpen: () => null,
}

const HamburgerContext = createContext<HamburgerContextType>(
  defaultHamburgerContext,
)

export const useHamburgerContext = () => {
  return useContext(HamburgerContext)
}

// Utility function
const getHamburgerOpen = (): boolean => {
  // client
  // prettier-ignore
  if (typeof window !== 'undefined') { // NOSONAR
    let open
    const from_localStorage = window.localStorage.getItem('dash.hamburger.open') // NOSONAR
    //console.log('from_localStorage(dash.hamburger.open)=', from_localStorage)
    if (from_localStorage) {
      open = from_localStorage === 'true'
    } else {
      // prettier-ignore
      window.localStorage.setItem( // NOSONAR
        'dash.hamburger.open',
        `${defaultHamburgerOpen}`,
      )
      open = defaultHamburgerOpen
    }
    return open
  }
  // server
  return defaultHamburgerOpen
}

export const HamburgerProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(getHamburgerOpen())

  const toggleIsOpen = () => {
    window.localStorage.setItem('dash.hamburger.open', `${!isOpen}`) // NOSONAR
    setIsOpen((open) => !open)
  }

  return (
    <HamburgerContext.Provider value={{ isOpen, toggleIsOpen, setIsOpen }}>
      {children}
    </HamburgerContext.Provider>
  )
}

// Hamburger UI based on: https://konradstaniszewski.com/blog/tailwind-hamburger
const Hamburger = () => {
  const genericHamburgerLine = `absolute scale-50 h-1 bg-current transition-all duration-300 motion-reduce:transition-none`
  const { isOpen, toggleIsOpen, setIsOpen } = useHamburgerContext()

  // Initialize
  useEffect(() => {}, [setIsOpen])

  // Update
  useEffect(() => {
    window.localStorage.setItem('dash.hamburger.open', `${isOpen}`) // NOSONAR
  }, [isOpen])

  return (
    <button
      type="button"
      aria-label="hamburger menu"
      tabIndex={0}
      className="focus-custom flex flex-col h-7 w-7 rounded-md justify-center items-center group border-custom"
      onClick={() => toggleIsOpen()}
    >
      <span
        className={`${genericHamburgerLine} ${
          isOpen ? 'top-4 w-12 rotate-45' : 'top-2 w-10'
        }`}
      />
      <span
        className={`${genericHamburgerLine} ${
          isOpen ? 'top-4 w-10 opacity-0 scale-x-0' : 'top-4 w-10 opacity-100'
        }`}
      />
      <span
        className={`${genericHamburgerLine} ${
          isOpen ? 'top-4 w-12 -rotate-45' : 'top-6 w-10'
        }`}
      />
    </button>
  )
}

export default Hamburger

export const HamburgerMenu = ({ children }: { children: React.ReactNode }) => {
  const { isOpen } = useHamburgerContext()

  useEffect(() => {
    //console.log('rerender menu-isOpen=', isOpen)
  }, [isOpen])

  console.log('HamburgerMenu-isOpen=', isOpen)
  return (
    // @@TODO: Strange: transition does not work with 'transition' added as directive, only works without it.
    <div
      className={`QQQtransition ease-in-out duration-300 motion-reduce:transition-none overflow-hidden ${
        isOpen ? 'max-w-40' : 'max-w-0'
      }`}
    >
      {children}
    </div>
  )
}
